import React from "react"
import { actionKeys } from "@love-irish/i18n"
import {
  IconDeviceGamepad2,
  IconPlayerPlay,
  IconSearch,
} from "@tabler/icons-react"
import Tooltip from "design_system/core/components/tooltip"
import Button from "@love-irish/core-button"
import useSound from "use-sound"
import { Input as LofiInput } from "@love-irish/core-lofi"
import tadhgAvatar from "images/profile_icons/tadhg.png"
import BaseListItem from "../base"
import "./index.css"

// Lo-fi preview banners keyed by mini-game format (falls back to the generic matcher grid).
const lofiPreviews: Record<string, () => React.ReactElement> = {
  "Item::MiniGame": () => (
    <div className="grid grid-cols-2 gap-8 h-[100px] justify-center">
      <div className="space-y-4">
        <LofiInput className="h-6 p-2 border-success bg-success-100" stringClassName="bg-success" />
        <LofiInput className="h-6 p-2" />
        <LofiInput className="h-6 p-2 border-dark bg-dark-100 mx-auto" stringClassName="bg-dark" />
      </div>
      <div className="space-y-4">
        <LofiInput className="h-6 p-2" />
        <LofiInput className="h-6 p-2 border-success bg-success-100" stringClassName="bg-success" />
        <LofiInput className="h-6 p-2" />
      </div>
    </div>
  ),
  word_guesser: () => (
    <div className="flex flex-col gap-4 h-[100px]">
      {[
        ["dark", "dark", "dark", "dark"],
        ["success", "orange", "", ""],
        ["success", "success", "", "orange"],
      ].map((row, i) => (
        <div key={i} className="text-center flex gap-2 items-center justify-center">
          {row.map((state, j) => (
            <LofiInput
              key={j}
              width="max-w-[20px]"
              className={
                state === "dark"
                  ? "h-6 p-2 border-dark bg-dark-100"
                  : state === "success"
                    ? "h-6 p-2 border-success bg-success-100"
                    : state === "orange"
                      ? "h-6 p-2 bg-orange-100 border-orange-500"
                      : "h-6 p-2"
              }
              stringClassName={
                state === "dark"
                  ? "bg-dark"
                  : state === "success"
                    ? "bg-success"
                    : state === "orange"
                      ? "bg-orange-500"
                      : undefined
              }
            />
          ))}
        </div>
      ))}
    </div>
  ),
}

export const MiniGameListItem = ({ item, className, footer, level = 0 }: any) => {
  const [play] = useSound(item.audioUrl || "")
  const PreviewComponent =
    lofiPreviews[item.miniGameFormat] || lofiPreviews["Item::MiniGame"]

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    play()
  }

  return (
    <BaseListItem
      item={item}
      className={className}
      typeIcon={IconDeviceGamepad2}
      typeLabel="Mini Game"
      typeTooltip="Practice words and earn experience, loot and other rewards"
      testId="mini-game-list-item"
      readMore={{ useI18n: true, actionKey: actionKeys.PLAY }}
      banner={
        <a href={item.path}>
          <div className="p-12 aspect-banner border-b">
            <PreviewComponent />
          </div>
        </a>
      }
      headerActions={
        <>
          <Tooltip title="Start game">
            <Button size="sm" variant="ghost" as="a" href={item.path}>
              <IconDeviceGamepad2 size={16} className="inline-block" />
            </Button>
          </Tooltip>
          {item.audioUrl && (
            <Tooltip title="Play featured community recording">
              <Button size="sm" variant="ghost" onClick={handlePlay}>
                <IconPlayerPlay size={16} className="inline-block" />
              </Button>
            </Tooltip>
          )}
        </>
      }
      media={
        item.imageUrl && (
          <div className="w-[256px] aspect-square pixel-art mt-4 shrink-0">
            <img loading="lazy" src={item.imageUrl} className="w-full block" alt={item.name} />
          </div>
        )
      }
    >
      {item.audioUrl && (
        <div className="mt-2">
          <div className="flex justify-start gap-2 bg-earth-50/70 text-earth-800 p-2 rounded border border-earth-200 items-center">
            <img loading="lazy" src={tadhgAvatar} className="w-[32px] pixel-art" alt="" />
            <button
              aria-label="Play audio"
              className="bg-none! cursor-pointer"
              onClick={handlePlay}
              type="button"
            >
              <IconPlayerPlay size={24} className="inline-block" />
            </button>
            <p>
              Featured community recording from{" "}
              <a href="/@tadhg" className="font-bold">
                @tadhg
              </a>
            </p>
          </div>
        </div>
      )}

      {/* EXP reward, shown when the game context opts in via `footer` (e.g. after a win). */}
      {footer && (
        <div className="mt-3 p-3 text-xs rounded border-t border-secondary-200 flex gap-2 bg-secondary-50 text-secondary-600 dark:bg-dark-900 dark:text-secondary-100 dark:border-dark-500">
          <IconSearch className="text-secondary-400 inline-block align-middle" size={14} />
          <div>
            {level === 1 ? "Unlocked a new codex record!" : "Found again!"}{" "}
            <span className="text-warning font-bold dark:text-warning-400">+ 5 EXP</span>
          </div>
        </div>
      )}
    </BaseListItem>
  )
}

export default MiniGameListItem
